import { Link } from "react-router-dom";
import { Check, Info, Printer, X } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/site/ProductCard";
import { useSeo } from "@/lib/seo";
import { GUIDE } from "@/lib/guide";
import { CHUTE_LINKS } from "@/lib/chuteGuides";
import { PregnancyNotice } from "@/components/site/PregnancyNotice";
import { FormationBox } from "@/components/site/FormationBox";

const List = ({ items, icon: Icon, testid }) => (
  <ul data-testid={testid} className="mt-4 space-y-3">
    {items.map((s) => (
      <li key={s} className="flex gap-3 text-sm leading-relaxed text-[var(--parchment)]/90 md:text-base">
        <Icon className="mt-1 h-4 w-4 shrink-0 text-[var(--gold)]" aria-hidden="true" />{s}
      </li>
    ))}
  </ul>
);

function Tracking() {
  return (
    <div className="mt-4 overflow-x-auto">
      <table data-testid="guide-tracking-table" className="w-full min-w-[520px] border-collapse text-sm">
        <thead><tr><th className="border border-[var(--line)] p-2 text-left">Zone</th>{GUIDE.tracking.map((c) => <th key={c} className="border border-[var(--line)] p-2 text-left">{c}</th>)}</tr></thead>
        <tbody>{GUIDE.zones.map((z) => <tr key={z}><td className="border border-[var(--line)] p-2">{z}</td>{GUIDE.tracking.map((c) => <td key={c} className="h-10 border border-[var(--line)] p-2" />)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export default function GuideRoutine() {
  const { data } = useProducts();
  const all = Array.isArray(data) ? data : [];
  const products = GUIDE.products.map((s) => all.find((p) => p.slug === s)).filter(Boolean);
  useSeo({ title: "Guide routine anti-chute cheveux afro | Ozentya", description: GUIDE.intro.slice(0, 155), path: "/guide-routine-anti-chute", image: "/editorial/guide-routine-cover.webp", noindex: true });

  return (
    <main data-testid="guide-page" className="guide-print mx-auto max-w-4xl px-5 pb-32 pt-44 md:px-10">
      <p className="eyebrow mb-6">Guide gratuit Ozentya · depuis 2011</p>
      <h1 data-testid="guide-title" className="font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">{GUIDE.title}</h1>
      <p className="mt-8 text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{GUIDE.intro}</p>
      <button data-testid="guide-print-button" onClick={() => window.print()} className="no-print mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ink)] hover:bg-[var(--gold-soft)]">
        <Printer className="h-4 w-4" aria-hidden="true" />Enregistrer en PDF / imprimer
      </button>
      <p data-testid="guide-notice" className="mt-8 flex gap-3 rounded-sm border border-[var(--gold)]/40 bg-[rgba(217,180,106,0.08)] p-5 text-sm leading-relaxed text-[var(--parchment)]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" aria-hidden="true" />{GUIDE.notice}
      </p>
      <PregnancyNotice testid="guide-pregnancy-notice" className="mt-4" />
      <section className="mt-16"><h2 className="font-display text-3xl">Avant de commencer</h2><List items={GUIDE.before} icon={Check} testid="guide-before" /></section>
      <section className="mt-16">
        <h2 className="font-display text-3xl">Le programme en 4 étapes</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {GUIDE.phases.map((ph, i) => (
            <div key={ph.weeks} data-testid={`guide-phase-${i + 1}`} className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
              <p className="eyebrow">{ph.weeks}</p>
              <h3 className="mt-2 font-display text-2xl italic text-[var(--gold-soft)]">{ph.name}</h3>
              <List items={ph.steps} icon={Check} />
            </div>
          ))}
        </div>
      </section>
      <section className="mt-16"><h2 className="font-display text-3xl">La routine des tempes et des contours</h2><List items={GUIDE.temples} icon={Check} testid="guide-temples" /></section>
      <section className="mt-16"><h2 className="font-display text-3xl">Les coiffures et habitudes à éviter</h2><List items={GUIDE.avoid} icon={X} testid="guide-avoid" /></section>
      <section className="mt-16"><h2 className="font-display text-3xl">Mon tableau de suivi</h2><p className="mt-3 text-sm text-[var(--parchment)]/80">Notez pour chaque zone : stable, mieux, moins bien. Gardez vos photos : elles seront utiles si vous consultez.</p><Tracking /></section>
      {products.length ? (
        <section data-testid="guide-products" className="no-print mt-20">
          <h2 className="mb-3 font-display text-3xl">Les soins Ozentya pour suivre ce programme</h2>
          <p className="mb-10 text-sm text-[var(--parchment)]/80">Ou recevez 2 soins au choix chaque mois avec la <Link to="/abonnement?formule=duo" data-testid="guide-duo-link" className="text-[var(--gold-soft)] underline">formule Duo à 19,99 € / mois</Link>, Point Relais offert en France, 3,99 € en Europe, sans engagement.</p>
          <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2">{products.map((p, i) => <ProductCard key={p.slug} p={p} index={i} details />)}</div>
        </section>
      ) : null}
      <FormationBox src="guide" testid="guide-routine-formation-box" className="no-print mt-16" />
      <section className="no-print mt-20">
        <h2 className="eyebrow mb-5">Nos guides chute de cheveux</h2>
        <div className="flex flex-wrap gap-2">
          {CHUTE_LINKS.map((l) => <Link key={l.to} to={l.to} data-testid={`guide-link-${l.to.slice(1)}`} className="flex h-10 items-center rounded-full border border-[var(--line)] px-5 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">{l.label}</Link>)}
        </div>
      </section>
    </main>
  );
}
